import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useWorkspace } from "@/hooks/use-workspace";
import { Loader2, Clock, AlertTriangle } from "lucide-react";

export interface OfficeHoursConfig {
  workspaceId?: string;
  enabled: boolean;
  daysOfWeek: number[];
  startLocal: string;
  endLocal: string;
  timezone: string;
}

export interface OfficeHoursResponse {
  config: OfficeHoursConfig;
  currentlyOpen: boolean;
  nextOpenAt: string | null;
}

interface OfficeHoursDeferral {
  id: string;
  scheduler: string;
  itemKind: string;
  itemId: string;
  nextOpenAt: string;
  deferredAt: string;
}

interface OfficeHoursDeferralsResponse {
  items: OfficeHoursDeferral[];
}

const DAY_LABELS: { value: number; label: string }[] = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

// Full IANA timezone list when the runtime supports Intl.supportedValuesOf
// (modern Chrome/Node). Falls back to a curated US/UTC list on older
// browsers so the picker is always populated.
const TIMEZONES: string[] = (() => {
  const intlAny = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  if (typeof intlAny.supportedValuesOf === "function") {
    try {
      const all = intlAny.supportedValuesOf("timeZone");
      if (Array.isArray(all) && all.length > 0) return all;
    } catch {
      // fall through to fallback
    }
  }
  return [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Phoenix",
    "America/Los_Angeles",
    "America/Anchorage",
    "Pacific/Honolulu",
    "UTC",
  ];
})();

// Mirror of the server-side wall-clock math so the open/closed badge can
// recompute every tick without a roundtrip. Keeps weekday + HH:mm parity
// using Intl in the configured timezone — no third-party tz library needed.
function computeLiveOfficeHours(
  cfg: OfficeHoursConfig,
  now: Date,
): { open: boolean; nextOpenAt: string | null } {
  if (!cfg.enabled) return { open: true, nextOpenAt: null };
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: cfg.timezone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const weekdayShort = parts.find((p) => p.type === "weekday")?.value ?? "Mon";
  const hourStr = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minStr = parts.find((p) => p.type === "minute")?.value ?? "00";
  const dowMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dow = dowMap[weekdayShort] ?? 1;
  const nowMinutes = parseInt(hourStr, 10) * 60 + parseInt(minStr, 10);
  const [sh, sm] = cfg.startLocal.split(":").map((n) => parseInt(n, 10));
  const [eh, em] = cfg.endLocal.split(":").map((n) => parseInt(n, 10));
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  const isOpenDay = cfg.daysOfWeek.includes(dow);
  const open = isOpenDay && nowMinutes >= startMin && nowMinutes < endMin;
  if (open) return { open: true, nextOpenAt: null };
  // Approximate next open by walking forward up to 8 days; keeps the UI in
  // sync with the server's nextOpenAt value to within the live tick.
  for (let dayOffset = 0; dayOffset < 8; dayOffset += 1) {
    const probe = new Date(now.getTime() + dayOffset * 86_400_000);
    const probeParts = fmt.formatToParts(probe);
    const probeDow = dowMap[probeParts.find((p) => p.type === "weekday")?.value ?? "Mon"] ?? 1;
    if (!cfg.daysOfWeek.includes(probeDow)) continue;
    if (dayOffset === 0 && nowMinutes >= startMin) continue;
    const probeStr = probe.toISOString().slice(0, 10);
    return { open: false, nextOpenAt: `${probeStr}T${cfg.startLocal}:00` };
  }
  return { open: false, nextOpenAt: null };
}

/**
 * Full office-hours editor — enabled toggle, days of week, start/end times,
 * and the workspace display/office timezone. The canonical source of truth is
 * the workspace_office_hours row, so this reads/writes the shared
 * GET/PUT /api/admin/office-hours endpoints. The timezone field here is the
 * same one that drives workspace-wide display timezone everywhere.
 *
 * Rendered both inside the Communications settings dialog and the admin
 * Calendar Settings screen (Task #416 moved it out of the now-removed
 * General section) so the two surfaces never diverge.
 */
export function OfficeHoursPanel() {
  const { toast } = useToast();
  // Task #416 — workspace-scoped path. The server fails closed with a
  // "Workspace context required" 400 on bare /api/admin/office-hours calls,
  // so route through buildPath (same fix class as Task #415's Calendar
  // Settings) and surface a real error instead of spinning forever.
  const { buildPath } = useWorkspace();
  const { data, isLoading, isError, error } = useQuery<OfficeHoursResponse>({
    queryKey: [buildPath("/api/admin/office-hours")],
    refetchInterval: 60_000,
  });

  const [draft, setDraft] = useState<OfficeHoursConfig | null>(null);
  const config = draft ?? data?.config ?? null;

  // Live tick: re-render every 15s so the displayed clock and the
  // computed open/closed badge advance without a refresh.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  const liveLocalTime = config
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: config.timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(now)
    : "";

  // Recompute open/closed entirely client-side from (config, now) so the
  // status updates between server roundtrips. We mirror the server math:
  // disabled=open; otherwise open iff today is in daysOfWeek and HH:mm is
  // in [startLocal, endLocal) in the chosen TZ.
  const liveStatus = config ? computeLiveOfficeHours(config, now) : null;
  const currentlyOpen = liveStatus ? liveStatus.open : !!data?.currentlyOpen;
  const liveNextOpenAt = liveStatus?.nextOpenAt ?? data?.nextOpenAt ?? null;

  const { data: deferrals } = useQuery<OfficeHoursDeferralsResponse>({
    queryKey: [buildPath("/api/admin/office-hours/deferrals")],
    refetchInterval: 60_000,
  });

  const save = useMutation({
    mutationFn: async (body: OfficeHoursConfig) => {
      const res = await apiRequest("PUT", buildPath("/api/admin/office-hours"), {
        enabled: body.enabled,
        daysOfWeek: body.daysOfWeek,
        startLocal: body.startLocal,
        endLocal: body.endLocal,
        timezone: body.timezone,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/admin/office-hours")] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspace/timezone"] });
      setDraft(null);
      toast({ title: "Office hours saved" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to save", description: err.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8" data-testid="text-office-hours-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Task #416 — real error fallback. Previously any non-success state (a
  // failed fetch included) left `config` null and re-rendered the spinner
  // forever. Now a genuine fetch failure surfaces an inline message.
  if (isError || !config) {
    return (
      <Alert variant="destructive" data-testid="alert-office-hours-error">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Couldn't load office hours
          {error instanceof Error && error.message ? `: ${error.message}` : "."} Please
          refresh the page to try again.
        </AlertDescription>
      </Alert>
    );
  }

  const toggleDay = (d: number) => {
    const next = config.daysOfWeek.includes(d)
      ? config.daysOfWeek.filter((x) => x !== d)
      : [...config.daysOfWeek, d].sort((a, b) => a - b);
    setDraft({ ...config, daysOfWeek: next });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Workspace Office Hours
            </CardTitle>
            <CardDescription>
              The timezone here is what every displayed date and time renders in across this
              workspace. When the envelope is enabled, all automated outbound (drip emails,
              campaigns, scheduled sends, SMS, reminders, recaps, AI calls) defers to the next
              opening when closed. Manual sends are never blocked.
            </CardDescription>
          </div>
          {data && (
            <Badge
              variant={currentlyOpen ? "default" : "secondary"}
              data-testid={`status-office-hours-${currentlyOpen ? "open" : "closed"}`}
            >
              {currentlyOpen ? "Open" : "Closed"}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {data && (
          <div
            className="rounded-md border bg-muted/40 px-3 py-2 text-sm"
            data-testid="text-office-hours-live-status"
          >
            Right now it's <span className="font-mono font-semibold">{liveLocalTime}</span> in{" "}
            <span className="font-mono">{config.timezone}</span> —{" "}
            <span className={currentlyOpen ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"}>
              {currentlyOpen ? "open" : "closed"}
            </span>
            {!currentlyOpen && liveNextOpenAt && (
              <span className="text-muted-foreground">
                {" "}
                · opens {new Date(liveNextOpenAt).toLocaleString(undefined, { timeZone: config.timezone })}
              </span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between">
          <Label htmlFor="office-hours-enabled">Enable office-hours envelope</Label>
          <input
            id="office-hours-enabled"
            type="checkbox"
            className="h-4 w-4"
            checked={config.enabled}
            onChange={(e) => setDraft({ ...config, enabled: e.target.checked })}
            data-testid="input-office-hours-enabled"
          />
        </div>

        <div className="space-y-2">
          <Label>Days of week</Label>
          <div className="flex flex-wrap gap-2">
            {DAY_LABELS.map((d) => {
              const on = config.daysOfWeek.includes(d.value);
              return (
                <Button
                  key={d.value}
                  type="button"
                  variant={on ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleDay(d.value)}
                  data-testid={`button-day-${d.value}`}
                >
                  {d.label}
                </Button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start-local">Start (local)</Label>
            <Input
              id="start-local"
              type="time"
              value={config.startLocal}
              onChange={(e) => setDraft({ ...config, startLocal: e.target.value })}
              data-testid="input-start-local"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end-local">End (local)</Label>
            <Input
              id="end-local"
              type="time"
              value={config.endLocal}
              onChange={(e) => setDraft({ ...config, endLocal: e.target.value })}
              data-testid="input-end-local"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tz">Timezone</Label>
          <Select
            value={config.timezone}
            onValueChange={(v) => setDraft({ ...config, timezone: v })}
          >
            <SelectTrigger id="tz" data-testid="select-timezone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {deferrals && deferrals.items.length > 0 && (
          <div className="rounded-md border bg-muted/20 px-3 py-2" data-testid="section-office-hours-deferrals">
            <div className="text-sm font-semibold mb-2">Recently held outbound</div>
            <ul className="text-xs space-y-1 max-h-48 overflow-auto">
              {deferrals.items.slice(0, 25).map((d) => (
                <li key={d.id} className="flex justify-between gap-2" data-testid={`row-deferral-${d.id}`}>
                  <span className="font-mono text-muted-foreground">{d.scheduler}</span>
                  <span className="font-mono">{d.itemKind}</span>
                  <span className="text-muted-foreground">
                    held {new Date(d.deferredAt).toLocaleString()} → resumes{" "}
                    {new Date(d.nextOpenAt).toLocaleString(undefined, { timeZone: config.timezone })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          {draft && (
            <Button
              variant="outline"
              onClick={() => setDraft(null)}
              disabled={save.isPending}
              data-testid="button-cancel-office-hours"
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={() => draft && save.mutate(draft)}
            disabled={!draft || save.isPending}
            data-testid="button-save-office-hours"
          >
            {save.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
